import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { companyNationalIdInputProps, yupCompanyNationalIdWhenLegal } from "../../../../../infrastructure/utils/companyNationalIdRules";
import { normalizeDigits, wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";

import DefaultApiService from "../../../../../infrastructure/api/default";
import RequestAssignmentApi from "../../../../../infrastructure/api/requestAssignment";
import MehrsunPortalApi from "../../../../../infrastructure/api/mehrsunPortalApi";
import {
  getApiFileBaseUrl,
  resolveUploadUrl,
} from "../../../../../infrastructure/utils/resolveUploadUrl";
import MapViewer from "../../../../components/map/MapViewer";
import AssignmentOperatorHelp from "../../../../components/request/AssignmentOperatorHelp";
import RequestConfirmChoiceGroup from "../../../../components/request/RequestConfirmChoiceGroup";
import RequestFollowUpTimeline from "../../../../components/request/RequestFollowUpTimeline";
import {
  canShowClaimAction,
  getCurrentUserId,
  normalizeAssignmentPermissions,
  persistUserIdentityToStorage,
  setAssignmentUserContext,
} from "../../../../components/request/requestAssignmentDisplay";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DistributionReferralChoicePanel from "../distribution/DistributionReferralChoicePanel";
import DropdownPicker from "../../../../components/shared/dropdown";
import EditableImageField from "../../../../components/shared/files/EditableImageField";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import { WORKSPACE_LIST_VIEWS } from "../projectWorkspace/projectWorkspacePaths";
import { SECTION_AFTER_INITIAL_REVIEW } from "../projectWorkspace/projectWorkspaceSectionProgress";
import { CHECK_NEW_REQUEST_CONFIG } from "./checkNewRequestConfig";
import {
  canOperatorEditRequestFields,
  getApplicantIdentityFields,
  getSavedRequestConfirm,
  getSavedStepInfoId,
  isInitialReviewComplete,
  isInitialReviewEditable,
  normalizeCheckNewRequestPageData,
  normalizeGenderFormValue,
  normalizeRequestEnumFields,
  normalizeRequestFileFields,
  parseApplicantBirthDate,
  formatBirthDateForApi,
  resolveGeoLookupData,
  resolveUploadedUrl,
} from "./checkNewRequestPageUtils";
import { InitialReviewWorkspace } from "./initialReview";

const isApproveConfirmStatus = (value) => {
  const normalized = String(value ?? "");
  return normalized === "InProcess" || normalized === "1";
};

const isNeedsCorrectionConfirmStatus = (value) => {
  const normalized = String(value ?? "");
  return normalized === "NotChecked" || normalized === "0";
};

const isPendingFollowUpConfirmStatus = (value) => {
  const normalized = String(value ?? "");
  return normalized === "PendingFollowUp" || normalized === "6";
};

const hasDistributionCompanySelected = (value) => {
  if (value == null || value === "") return false;
  const num = Number(value);
  return !Number.isNaN(num) && num > 0;
};

const validationSchema = Yup.object().shape({
  // TODO: فعلاً کامنت شده
  provinceId: Yup.mixed().required("لطفا استان را انتخاب کنید."),
  cityId: Yup.mixed().required("لطفا شهرستان را انتخاب کنید."),
  distributionCompanyId: Yup.mixed()
    .required("لطفا شرکت توزیع را انتخاب کنید.")
    .test(
      "distribution-company-required",
      "لطفا شرکت توزیع را انتخاب کنید.",
      (value) => hasDistributionCompanySelected(value)
    ),
  // substationId: Yup.number().required("لطفا این فیلد را پر کنید."),
  // feederId: Yup.number().required("لطفا این فیلد را پر کنید."),

  firstName: Yup.string().required("لطفا این فیلد را پر کنید."),
  lastName: Yup.string().required("لطفا این فیلد را پر کنید."),
  mobile: Yup.string().required("لطفا این فیلد را پر کنید."),
  nationalCode: Yup.string()
    .required("کد ملی الزامی است.")
    .test("national-code", "کد ملی باید ۱۰ رقم باشد.", (value) =>
      /^\d{10}$/.test(normalizeDigits(String(value || "")).trim())
    ),

  address: Yup.string().required("لطفا این فیلد را پر کنید."),

  postalCode: Yup.string().required("لطفا این فیلد را پر کنید."),

  plantCapacity: Yup.string().required("لطفا این فیلد را پر کنید."),
  electricBoillNumber: Yup.string()
    .trim()
    .required("شناسه قبض الزامی است."),
  xPoint: Yup.number().required("لطفا این فیلد را پر کنید."),
  yPoint: Yup.number().required("لطفا این فیلد را پر کنید."),
  note: Yup.string(),
  supervisorInfoId: Yup.string().nullable(),
  companyNationalCode: yupCompanyNationalIdWhenLegal(),

  plantInstallImage1Url: Yup.string().required(
    "لطفا تصویر اول محل نصب را آپلود کنید."
  ),
  plantInstallImage2Url: Yup.string().required(
    "لطفا تصویر دوم محل نصب را آپلود کنید."
  ),
});

const locationUsageTypeOptions = [
  { name: "مسکونی", value: "1" },
  { name: "تجاری", value: "2" },
  { name: "اداری", value: "3" },
  { name: "صنعتی", value: "4" },
  { name: "کشاورزی", value: "5" },
  { name: "باغ و ویلا", value: "6" },
  { name: "مسجد / مذهبی", value: "7" },
  { name: "آموزشی", value: "8" },
  { name: "سایر", value: "9" },
];

const ownershipStatusOptions = [
  { name: "مالک هستم", value: "1" },
  { name: "دارای مجوز استفاده", value: "3" },
  { name: "نیاز به بررسی", value: "4" },
];

const platformAcquaintanceSourceOptions = [
  { name: "مهرسان", value: "1" },
  { name: "شبکه های اجتماعی", value: "2" },
  { name: "دوستان", value: "3" },
  { name: "سرچ گوگل", value: "4" },
  { name: "سایر", value: "5" },
];

const applicantGoalOptions = [
  { label: "فروش برق به شبکه", value: 1 },
  { label: "کاهش هزینه برق", value: 2 },
  { label: "برق اضطراری", value: 4 },
  { label: "استقلال از شبکه", value: 8 },
  { label: "سرمایه‌گذاری", value: 16 },
  { label: "نیاز به مشاوره", value: 32 },
];

const applicantGenderOptions = [
  { name: "مرد", value: "1" },
  { name: "زن", value: "2" },
];

const normalizeEnumList = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    name: item?.name ?? item?.Name ?? "",
    value: String(item?.value ?? item?.Value ?? ""),
  }));

const displayIdentityValue = (value) => {
  if (value == null || value === "") return "—";
  return String(value);
};

const FORM_FIELD_SIZE = "small";
const FORM_GRID_SPACING = 1.5;
const FORM_SECTION_SPACING = 1.5;

const FormSection = ({
  title,
  subtitle,
  icon: Icon,
  children,
  collapsible = false,
  defaultExpanded = true,
  compact = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: "divider",
        boxShadow: "none",
        overflow: "visible",
      }}
    >
      <CardHeader
        avatar={
          Icon ? (
            <Box
              sx={{
                width: compact ? 28 : 36,
                height: compact ? 28 : 36,
                borderRadius: 1,
                bgcolor: "primary.50",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: compact ? 16 : 20 }} />
            </Box>
          ) : null
        }
        title={title}
        subheader={subtitle}
        titleTypographyProps={{
          variant: compact ? "subtitle2" : "subtitle1",
          fontWeight: 700,
        }}
        subheaderTypographyProps={{ variant: "caption" }}
        action={
          collapsible ? (
            <IconButton
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              aria-label={expanded ? "بستن بخش" : "باز کردن بخش"}
            >
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          ) : null
        }
        sx={{
          py: compact ? 0.75 : 1.5,
          px: compact ? 1.5 : 2,
          "& .MuiCardHeader-content": { minWidth: 0 },
        }}
      />
      <Collapse in={!collapsible || expanded}>
        <CardContent sx={{ pt: 0, px: compact ? 1.5 : 2, pb: compact ? 1.5 : 2 }}>
          {children}
        </CardContent>
      </Collapse>
    </Card>
  );
};

const RequestSummaryBar = ({ values }) => {
  const chips = [
    values.plantCapacityTitle || values.plantCapacity
      ? {
        label: `ظرفیت: ${values.plantCapacityTitle || values.plantCapacity}`,
        color: "primary",
      }
      : null,
    values.locationUsageTypeTitle
      ? { label: values.locationUsageTypeTitle, color: "default" }
      : null,
    values.installableAreaSquareMeter
      ? { label: `${values.installableAreaSquareMeter} متر مربع`, color: "default" }
      : null,
    values.ownershipStatusTitle
      ? { label: values.ownershipStatusTitle, color: "default" }
      : null,
    values.platformAcquaintanceSourceTitle
      ? { label: values.platformAcquaintanceSourceTitle, color: "default" }
      : null,
    {
      label: values.needsLoan ? "نیاز به وام" : "بدون وام",
      color: values.needsLoan ? "warning" : "default",
    },
    values.requestConfirmTitle
      ? { label: values.requestConfirmTitle, color: "info" }
      : null,
  ].filter(Boolean);

  if (!chips.length) return null;

  return (
    <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
      {chips.map((chip) => (
        <Chip
          key={chip.label}
          label={chip.label}
          size="small"
          color={chip.color}
          variant={chip.color === "default" ? "outlined" : "filled"}
        />
      ))}
    </Stack>
  );
};

const ApplicantInfoSection = ({
  values,
  fieldDisabled,
  handleChange,
  onMakeFormikVariable,
  setFieldValue,
  formData,
  errors,
  touched,
}) => {
  const identity = getApplicantIdentityFields(values);

  return (
    <FormSection
      title="اطلاعات متقاضی"
      subtitle="اطلاعات هویتی و تماس متقاضی"
      icon={PersonOutlineIcon}
    >
      <Grid container spacing={FORM_GRID_SPACING}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="کد ملی"
            onChange={wrapFormikHandleChange(handleChange)}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            {...onMakeFormikVariable("nationalCode")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="نام پدر"
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("fatherName")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <JalaliDatePicker
            label="تاریخ تولد"
            value={values.birthDate}
            onChange={(date) => setFieldValue("birthDate", date)}
            error={errors.birthDate && touched.birthDate}
            helperText={
              errors.birthDate && touched.birthDate && errors.birthDate
            }
            disabled={fieldDisabled}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <DropdownPicker
            size="small"
            label="جنسیت"
            list={applicantGenderOptions}
            optionName="name"
            optionValue="value"
            filterable={false}
            value={
              values.gender != null && values.gender !== ""
                ? String(values.gender)
                : ""
            }
            onChange={(event) => setFieldValue("gender", event.target.value)}
            disabled={fieldDisabled}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="نام کاربری"
            value={displayIdentityValue(identity.userName)}
            fullWidth
            variant="outlined"
            size="small"
            disabled
            InputProps={{ readOnly: true }}
            sx={{ "& .MuiInputBase-root": { bgcolor: "grey.50" } }}
          />
        </Grid>

        {values.agreementKind === "Legal" ? (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="شناسه ملی شرکت"
                onChange={wrapFormikHandleChange(handleChange)}
                inputProps={companyNationalIdInputProps}
                {...onMakeFormikVariable("companyNationalCode")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="شماره ثبت شرکت"
                onChange={wrapFormikHandleChange(handleChange)}
                {...onMakeFormikVariable("companyRegisterationCode")}
              />
            </Grid>
          </>
        ) : null}

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="نام متقاضی"
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("firstName")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="نام خانوادگی متقاضی"
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("lastName")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="همراه متقاضی"
            {...onMakeFormikVariable("mobile")}
            disabled
            InputProps={{ readOnly: true }}
            sx={{ "& .MuiInputBase-root": { bgcolor: "grey.50" } }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <DropdownPicker
            size="small"
            label="نوع شخص"
            list={formData?.agreementKind || []}
            optionName="name"
            optionValue="value"
            filterable={false}
            value={
              values.agreementKind != null && values.agreementKind !== ""
                ? String(values.agreementKind)
                : ""
            }
            onChange={(event) => {
              setFieldValue("agreementKind", event.target.value);
            }}
            disabled={fieldDisabled}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="تلفن ثابت"
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("telephon")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="ایمیل"
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("email")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="کد پستی"
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("postalCode")}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

const CheckNewRequestModifyPage = ({
  config = CHECK_NEW_REQUEST_CONFIG.solarentOperator,
  embedded = false,
  embeddedPayload = null,
  onRefreshEmbedded = null,
  onRegisterFollowUp = null,
  workflowStageTitle = "",
  workflowSource = "",
  workspaceDetailApi = "",
  onNavigateToSection = null,
  workspaceListView = "",
  workspaceEditIntent = false,
}) => {
  const { apiPath, listPath, assignmentSource } = config;
  const routeLoaderPayload = useLoaderData();
  const loaderPayload = embedded ? embeddedPayload : routeLoaderPayload;
  const data = useMemo(
    () => normalizeCheckNewRequestPageData(loaderPayload),
    [loaderPayload]
  );
  let navigate = useNavigate();
  const { id: requestId } = useParams();

  const [loading, setLoading] = useState(false);
  const submitLockRef = useRef(false);
  const [pageData, setPageData] = useState(data);
  const [followUps, setFollowUps] = useState(data?.followUps || []);
  const [actionHistory, setActionHistory] = useState(
    data?.requestUpdateHistoryList || []
  );
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentFeatureEnabled, setAssignmentFeatureEnabled] = useState(false);
  const [assignmentFeatureChecked, setAssignmentFeatureChecked] = useState(false);
  const [canClaimRequests, setCanClaimRequests] = useState(false);
  const [statusUserKind, setStatusUserKind] = useState(null);
  const [statusUserRoleName, setStatusUserRoleName] = useState(null);
  const [distributionCompanies, setDistributionCompanies] = useState([]);

  useEffect(() => {
    if (data) {
      setPageData(data);
      setFollowUps(data.followUps || []);
      setActionHistory(data.requestUpdateHistoryList || []);
    }
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await MehrsunPortalApi.getCompanyOptions(assignmentSource);
        if (cancelled) return;
        const list = res?.data ?? res?.Data;
        setDistributionCompanies(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setDistributionCompanies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentSource]);

  const formData = pageData || data;
  const fileBaseUrl = useMemo(() => {
    const fromApi = getApiFileBaseUrl(formData);
    if (fromApi) return fromApi;
    return getApiFileBaseUrl(window.globalConfig?.API_URL || "");
  }, [formData]);
  const request = useMemo(
    () =>
      normalizeRequestEnumFields(
        normalizeRequestFileFields(
          normalizeAssignmentPermissions(formData?.request)
        )
      ),
    [formData?.request]
  );
  const currentUserId = getCurrentUserId();
  const assignedId = request?.assignedOperatorUserId ?? null;
  const assignmentOwnerLabel = !assignedId
    ? "بدون مسئول"
    : currentUserId && assignedId === currentUserId
      ? "شما"
      : request?.assignedOperatorFullName || "—";

  const operatorCanEditAllFields = canOperatorEditRequestFields(
    request,
    assignmentSource
  );

  const isDataOnlyEditMode =
    Boolean(request) &&
    !isInitialReviewEditable(request);

  const refreshRequest = async () => {
    if (!requestId || requestId === "create") return null;
    const res = await DefaultApiService.getOneApi(apiPath, requestId);
    if (res?.isSuccess && res.data) {
      const payload = normalizeCheckNewRequestPageData(res);
      setPageData(payload);
      setFollowUps(payload?.followUps || []);
      setActionHistory(payload?.requestUpdateHistoryList || []);
      if (payload?.request?.canClaim || payload?.request?.canRelease) {
        setAssignmentFeatureEnabled(true);
      }
      return payload?.request ?? null;
    }
    return null;
  };

  const handleClaim = async () => {
    setAssignmentLoading(true);
    try {
      const res = await RequestAssignmentApi.claim(
        assignmentSource,
        Number(requestId)
      );
      if (res?.isSuccess) {
        toast.success(res.message || "درخواست با موفقیت دریافت شد.");
        await refreshRequest();
      } else {
        toast.error(
          res?.message || "این درخواست قبلاً توسط کارشناس دیگری دریافت شده است."
        );
        await refreshRequest();
      }
    } catch {
      toast.error("خطا در دریافت درخواست");
      await refreshRequest();
    } finally {
      setAssignmentLoading(false);
    }
  };

  useEffect(() => {
    RequestAssignmentApi.getStatus(assignmentSource).then((status) => {
      setAssignmentFeatureEnabled(status.enabled);
      setCanClaimRequests(status.canClaimRequests);
      setStatusUserKind(status.currentUserKind);
      setStatusUserRoleName(status.currentUserRoleName);
      setAssignmentUserContext({
        canClaimRequests: status.canClaimRequests,
        currentUserKind: status.currentUserKind,
        currentUserId: status.currentUserId,
      });
      persistUserIdentityToStorage({
        userKind: status.currentUserKind,
        userId: status.currentUserId,
      });
      setAssignmentFeatureChecked(true);
    });
  }, [assignmentSource]);

  const handleRelease = async () => {
    setAssignmentLoading(true);
    try {
      const res = await RequestAssignmentApi.release(
        assignmentSource,
        Number(requestId)
      );
      if (res?.isSuccess) {
        toast.success(res.message || "درخواست آزاد شد و بدون مسئول است.");
        await refreshRequest();
      } else {
        toast.error(res?.message || "خطا در آزادسازی درخواست");
      }
    } catch {
      toast.error("خطا در آزادسازی درخواست");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const onNavigateToListPage = () => {
    if (embedded) {
      onRefreshEmbedded?.();
      return;
    }
    navigate(listPath);
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting } = {}) => {
    if (submitLockRef.current) return;

    const electricBoillNumberCheck = String(
      formValue.electricBoillNumber ?? ""
    ).trim();
    if (!electricBoillNumberCheck) {
      toast.error("شناسه قبض الزامی است.");
      setSubmitting?.(false);
      return;
    }

    if (!hasDistributionCompanySelected(formValue.distributionCompanyId)) {
      toast.error("لطفا شرکت توزیع را انتخاب کنید.");
      setSubmitting?.(false);
      return;
    }

    submitLockRef.current = true;
    setLoading(true);
    setSubmitting?.(true);

    try {
    const plantInstallImage1Url = resolveUploadedUrl(
      formValue.plantInstallImage1Url,
      request?.plantInstallImage1Url
    );
    const plantInstallImage2Url = resolveUploadedUrl(
      formValue.plantInstallImage2Url,
      request?.plantInstallImage2Url
    );
    const nationalCardUrl = resolveUploadedUrl(
      formValue.nationalCardUrl,
      request?.nationalCardUrl
    );
    const birthCertificateUrl = resolveUploadedUrl(
      formValue.birthCertificateUrl,
      request?.birthCertificateUrl
    );
    const nationalCardBackUrl = resolveUploadedUrl(
      formValue.nationalCardBackUrl,
      request?.nationalCardBackUrl
    );
    const electricBoillUrl = resolveUploadedUrl(
      formValue.electricBoillUrl,
      request?.electricBoillUrl
    );
    const homeOwnerShipDocUrl = resolveUploadedUrl(
      formValue.homeOwnerShipDocUrl,
      request?.homeOwnerShipDocUrl
    );
    const boardConsentDocumentUrl = resolveUploadedUrl(
      formValue.boardConsentDocumentUrl,
      request?.boardConsentDocumentUrl
    );
    const authorizedRepresentativeLetterUrl = resolveUploadedUrl(
      formValue.authorizedRepresentativeLetterUrl,
      request?.authorizedRepresentativeLetterUrl
    );
    const agreementDocumentUrl = resolveUploadedUrl(
      formValue.agreementDocumentUrl,
      request?.agreementDocumentUrl
    );
    const electricBoillNumber =
      String(formValue.electricBoillNumber ?? "").trim() ||
      String(request?.electricBoillNumber ?? request?.ElectricBoillNumber ?? "").trim();

    const savedRequestConfirm = getSavedRequestConfirm(request);
    const savedStepInfoId = getSavedStepInfoId(request);
    const savedMobile = request?.mobile ?? request?.Mobile ?? formValue.mobile;
    const savedSupervisorInfoId =
      request?.supervisorInfoId ?? request?.SupervisorInfoId ?? null;

    const sendData = {
      id: formValue.id,
      stepInfoId: isDataOnlyEditMode ? savedStepInfoId : formValue.stepInfoId,
      saveDataOnly: isDataOnlyEditMode,
      companyId: data.substation?.companyId || formValue.companyId,
      provinceId: formValue.provinceId, // Hidden field - sent back unchanged
      cityId: formValue.cityId || 47, // پیش‌فرض 47
      distributionCompanyId: formValue.distributionCompanyId
        ? Number(formValue.distributionCompanyId)
        : null,
      substationId: formValue.substationId || 1, // پیش‌فرض 1
      feederId: formValue.feederId || 1, // پیش‌فرض 1
      userId: formValue.userId,
      customerInfoId: formValue.customerInfoId,

      // اطلاعات فرم
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      nationalCode: normalizeDigits(String(formValue.nationalCode || "")).trim(),
      mobile: isDataOnlyEditMode ? savedMobile : formValue.mobile,
      telephon: formValue.telephon,
      email: formValue.email,
      fatherName: formValue.fatherName,
      birthDate: formatBirthDateForApi(formValue.birthDate),
      gender:
        formValue.gender !== "" && formValue.gender != null
          ? Number(formValue.gender)
          : null,
      agreementKind: formValue.agreementKind,
      companyNationalCode: formValue.companyNationalCode,
      companyRegisterationCode: formValue.companyRegisterationCode,
      address: formValue.address,
      postalCode: formValue.postalCode,

      // اطلاعات نیروگاه
      plantKind: formValue.plantKind,
      powerPlantType:
        formValue.powerPlantType !== "" && formValue.powerPlantType != null
          ? Number(formValue.powerPlantType)
          : null,
      locationUsageType:
        formValue.locationUsageType !== "" && formValue.locationUsageType != null
          ? Number(formValue.locationUsageType)
          : null,
      installableAreaSquareMeter:
        formValue.installableAreaSquareMeter !== "" &&
          formValue.installableAreaSquareMeter != null
          ? Number(formValue.installableAreaSquareMeter)
          : null,
      ownershipStatus:
        formValue.ownershipStatus !== "" && formValue.ownershipStatus != null
          ? Number(formValue.ownershipStatus)
          : null,
      platformAcquaintanceSource:
        formValue.platformAcquaintanceSource !== "" &&
          formValue.platformAcquaintanceSource != null
          ? Number(formValue.platformAcquaintanceSource)
          : null,
      applicantGoalFlags:
        formValue.applicantGoalFlags !== "" && formValue.applicantGoalFlags != null
          ? Number(formValue.applicantGoalFlags)
          : null,
      plantCapacity: formValue.plantCapacity,
      installationType:
        formValue.installationType !== "" && formValue.installationType != null
          ? Number(formValue.installationType)
          : request?.installationType ?? request?.InstallationType ?? 0,
      unitCount:
        formValue.unitCount !== "" && formValue.unitCount != null
          ? Number(formValue.unitCount)
          : request?.unitCount ?? request?.UnitCount ?? null,
      electricBoillNumber,
      needsLoan: Boolean(formValue.needsLoan),
      NeedsLoan: Boolean(formValue.needsLoan),
      xPoint: formValue.xPoint,
      yPoint: formValue.yPoint,
      note: formValue.note,
      requestConfirm: isDataOnlyEditMode
        ? savedRequestConfirm
        : formValue.requestConfirm,
      // SMS opt-in: approval uses NotifyApplicant; needs-correction uses NotifyApplicantNeedsCorrection.
      notifyApplicant: isDataOnlyEditMode
        ? false
        : Boolean(formValue.notifyApplicant) &&
          isApproveConfirmStatus(formValue.requestConfirm),
      notifyApplicantNeedsCorrection: isDataOnlyEditMode
        ? false
        : Boolean(formValue.notifyApplicant) &&
          !isApproveConfirmStatus(formValue.requestConfirm) &&
          isNeedsCorrectionConfirmStatus(formValue.requestConfirm),
      notifyApplicantPendingFollowUp: isDataOnlyEditMode
        ? false
        : Boolean(formValue.notifyApplicant) &&
          isPendingFollowUpConfirmStatus(formValue.requestConfirm),
      adminComment: formValue.adminComment,
      supervisorInfoId: isDataOnlyEditMode
        ? savedSupervisorInfoId != null &&
          String(savedSupervisorInfoId).trim() !== ""
          ? savedSupervisorInfoId
          : null
        : formValue.supervisorInfoId != null &&
          String(formValue.supervisorInfoId).trim() !== ""
          ? formValue.supervisorInfoId
          : null,

      plantInstallImage1Url,
      plantInstallImage2Url,
      nationalCardUrl,
      birthCertificateUrl,
      nationalCardBackUrl,
      electricBoillUrl,
      homeOwnerShipDocUrl,
      boardConsentDocumentUrl,
      authorizedRepresentativeLetterUrl,
      agreementDocumentUrl,

      PlantInstallImage1Url: plantInstallImage1Url,
      PlantInstallImage2Url: plantInstallImage2Url,
      NationalCardUrl: nationalCardUrl,
      BirthCertificateUrl: birthCertificateUrl,
      NationalCardBackUrl: nationalCardBackUrl,
      ElectricBoillUrl: electricBoillUrl,
      ElectricBoillNumber: electricBoillNumber,
      HomeOwnerShipDocUrl: homeOwnerShipDocUrl,
      BoardConsentDocumentUrl: boardConsentDocumentUrl,
      AuthorizedRepresentativeLetterUrl: authorizedRepresentativeLetterUrl,
      AgreementDocumentUrl: agreementDocumentUrl,
    };

      const res = await DefaultApiService.modifyApi(apiPath, sendData);

      if (res.isSuccess) {
        toast.success(res.message);
        if (embedded) {
          const updatedRequest = await refreshRequest();
          await onRefreshEmbedded?.();
          if (
            !isDataOnlyEditMode &&
            onNavigateToSection &&
            isInitialReviewComplete(
              normalizeRequestFileFields(updatedRequest)
            )
          ) {
            onNavigateToSection(SECTION_AFTER_INITIAL_REVIEW);
          }
        } else {
          onNavigateToListPage();
        }
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      const errorList = error?.response?.data;
      if (typeof errorList === "object") {
        for (const key in errorList) {
          if (Object.hasOwnProperty.call(errorList, key)) {
            const list = errorList[key];
            toast.error(list[0]);
          }
        }
      } else toast.error("خطایی رخ داده است.");
    } finally {
      submitLockRef.current = false;
      setLoading(false);
      setSubmitting?.(false);
    }
  };

  const formBody = (
    <CardContent sx={embedded ? { p: 0, "&:last-child": { pb: 0 } } : undefined}>
      {!request && requestId !== "create" ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          بارگذاری اطلاعات درخواست ناموفق بود. از لیست دوباره «ویرایش» را
          بزنید یا صفحه را رفرش کنید.
        </Alert>
      ) : null}
      {request?.id || request?.Id ? (
        <DistributionReferralChoicePanel
          requestId={request.id ?? request.Id}
        />
      ) : null}
      {!embedded ? (
        <>
          <AssignmentOperatorHelp
            showAssignmentUi={assignmentFeatureEnabled}
            featureChecked={assignmentFeatureChecked}
            canClaimRequests={canClaimRequests}
            currentUserKind={statusUserKind}
            currentUserRoleName={statusUserRoleName}
            hasClaimableRow={canShowClaimAction(request, assignmentFeatureEnabled)}
            compact={
              assignmentFeatureEnabled &&
              canShowClaimAction(request, assignmentFeatureEnabled)
            }
          />
          {request && assignmentFeatureEnabled && (
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                mb: 1.5,
                borderRadius: 1.5,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    مسئول پیگیری:
                  </Typography>
                  <Chip
                    label={assignmentOwnerLabel}
                    color={
                      assignedId && currentUserId && assignedId === currentUserId
                        ? "success"
                        : "default"
                    }
                    variant={assignedId ? "filled" : "outlined"}
                  />
                  {request.assignedAt && (
                    <Typography variant="caption" color="text.secondary">
                      تاریخ دریافت: {toJalaliFullDate(request.assignedAt)}
                    </Typography>
                  )}
                  {request.assignmentSourceTitle && (
                    <Typography variant="caption" color="text.secondary">
                      {request.assignmentSourceTitle}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.75}>
                  {canShowClaimAction(request, assignmentFeatureEnabled) && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={assignmentLoading}
                      onClick={handleClaim}
                    >
                      دریافت درخواست
                    </Button>
                  )}
                  {request.canRelease && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      disabled={assignmentLoading}
                      onClick={handleRelease}
                    >
                      آزادسازی
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          )}
        </>
      ) : null}

      {request ? (
        <Formik
          enableReinitialize
          initialValues={{
            ...request,
            id: request.id ?? requestId,
            // Hidden field - preserve provinceId from server
            provinceId: request?.provinceId ?? request?.ProvinceId ?? "",
            cityId: request?.cityId ?? request?.CityId ?? "",
            distributionCompanyId:
              request?.distributionCompanyId ??
              request?.DistributionCompanyId ??
              "",
            // Normalize image fields from API (fallback to non-Url keys if present)
            plantInstallImage1Url: request?.plantInstallImage1Url || "",
            plantInstallImage2Url: request?.plantInstallImage2Url || "",
            nationalCardUrl: request?.nationalCardUrl || "",
            birthCertificateUrl: request?.birthCertificateUrl || "",
            nationalCardBackUrl: request?.nationalCardBackUrl || "",
            electricBoillUrl: request?.electricBoillUrl || "",
            electricBoillNumber:
              request?.electricBoillNumber || request?.ElectricBoillNumber || "",
            homeOwnerShipDocUrl: request?.homeOwnerShipDocUrl || "",
            boardConsentDocumentUrl: request?.boardConsentDocumentUrl || request?.BoardConsentDocumentUrl || "",
            authorizedRepresentativeLetterUrl: request?.authorizedRepresentativeLetterUrl || request?.AuthorizedRepresentativeLetterUrl || "",
            agreementDocumentUrl: request?.agreementDocumentUrl || request?.AgreementDocumentUrl || "",
            installationType: request?.installationType || request?.InstallationType || 0,
            needsLoan: request?.needsLoan !== undefined ? request?.needsLoan : (request?.NeedsLoan !== undefined ? request?.NeedsLoan : false),
            // Fix typo and prefer existing admin comment if any
            adminComment: request?.adminComment || "",
            supervisorInfoId: request?.supervisorInfoId || request?.SupervisorInfoId || "",
            powerPlantType: request?.powerPlantType ?? "",
            locationUsageType: request?.locationUsageType ?? "",
            ownershipStatus: request?.ownershipStatus ?? "",
            platformAcquaintanceSource: request?.platformAcquaintanceSource ?? "",
            installableAreaSquareMeter:
              request?.installableAreaSquareMeter ?? "",
            applicantGoalFlags:
              request?.applicantGoalFlags != null
                ? Number(request.applicantGoalFlags)
                : 0,
            fatherName: request?.fatherName ?? request?.FatherName ?? "",
            nationalCode:
              request?.nationalCode ?? request?.NationalCode ?? "",
            birthDate: parseApplicantBirthDate(
              request?.birthDate ?? request?.BirthDate
            ),
            gender: normalizeGenderFormValue(
              request?.gender ?? request?.Gender
            ),
            notifyApplicant: true,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            setSubmitting,
          }) => {
            const handleFileChange = (fieldName) => (value) => {
              if (value === undefined || value === null) {
                return;
              }
              setFieldValue(fieldName, value);
            };

            const isEditable =
              operatorCanEditAllFields ||
              isInitialReviewEditable(request, {
                forceEditable: operatorCanEditAllFields,
              });
            const fieldsEditable = isEditable;
            const fieldDisabled = !isEditable || isSubmitting || loading;
            const powerPlantTypeList = normalizeEnumList(formData?.powerPlantType);
            const { provinceList, cityList: allCityList } =
              resolveGeoLookupData(formData, data);
            const filteredCityList = allCityList.filter((x) => {
              const currentProvinceId =
                values.provinceId ?? values.ProvinceId;
              if (!currentProvinceId) return false;
              return (
                String(x.provinceId) === String(currentProvinceId) ||
                Number(x.provinceId) === Number(currentProvinceId)
              );
            });

            const onMakeFormikVariable = (name) => ({
              name: name,
              fullWidth: true,
              size: FORM_FIELD_SIZE,
              value: values[name],
              error: errors[name] && touched[name],
              helperText: errors[name] && touched[name] && errors[name],
              variant: "outlined",
              disabled: fieldDisabled,
            });

            return (
              <Fragment>
                <form onSubmit={handleSubmit}>
                  {!embedded && !isEditable ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      این درخواست از مرحله بررسی اولیه خارج شده و فقط برای
                      مشاهده است.
                    </Alert>
                  ) : null}

                  <InitialReviewWorkspace
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    setFieldValue={setFieldValue}
                    request={request}
                    formData={formData}
                    data={formData}
                    fieldDisabled={fieldDisabled}
                    isEditable={isEditable}
                    fileBaseUrl={fileBaseUrl}
                    followUps={followUps}
                    actionHistory={actionHistory}
                    onRegisterFollowUp={onRegisterFollowUp}
                    onMakeFormikVariable={onMakeFormikVariable}
                    handleFileChange={handleFileChange}
                    powerPlantTypeList={powerPlantTypeList}
                    workflowStageTitle={workflowStageTitle}
                    workflowSource={workflowSource || assignmentSource}
                    requestInfoId={requestId}
                    detailApi={
                      workspaceDetailApi ||
                      (assignmentSource === "Management"
                        ? "SLN/Management/AllRequest"
                        : "SLN/SolarentOperator/AllRequests")
                    }
                    assignmentOwnerLabel={assignmentOwnerLabel}
                    assignmentFeatureEnabled={assignmentFeatureEnabled}
                    assignmentFeatureChecked={assignmentFeatureChecked}
                    canClaimRequests={canClaimRequests}
                    statusUserKind={statusUserKind}
                    statusUserRoleName={statusUserRoleName}
                    canShowClaim={canShowClaimAction(
                      request,
                      assignmentFeatureEnabled
                    )}
                    canRelease={Boolean(request?.canRelease)}
                    assignmentLoading={assignmentLoading}
                    onClaim={handleClaim}
                    onRelease={handleRelease}
                    loading={loading}
                    onNavigateToSection={onNavigateToSection}
                    hideReferAction={embedded}
                    distributionCompanies={distributionCompanies}
                    creditInquiryApiPath={apiPath}
                    hideReviseAction={
                      embedded &&
                      !workspaceEditIntent &&
                      workspaceListView !== WORKSPACE_LIST_VIEWS.checkNew
                    }
                    dataOnlyEditMode={isDataOnlyEditMode}
                    onQuickStatusSubmit={(statusValue, options = {}) => {
                      const billId = String(
                        values?.electricBoillNumber || ""
                      ).trim();
                      if (!billId) {
                        toast.error("شناسه قبض الزامی است.");
                        setFieldTouched?.("electricBoillNumber", true, true);
                        return;
                      }
                      if (!hasDistributionCompanySelected(values?.distributionCompanyId)) {
                        toast.error("لطفا شرکت توزیع را انتخاب کنید.");
                        setFieldTouched?.("distributionCompanyId", true, true);
                        return;
                      }
                      const isReject =
                        String(statusValue) === "2" ||
                        String(statusValue).toLowerCase() === "canceled";
                      const comment = String(values?.adminComment || "").trim();
                      if (isReject && !comment) {
                        toast.error(
                          "برای رد/لغو درخواست، ابتدا دلیل را در فیلد توضیح کارشناس وارد کنید."
                        );
                        return;
                      }
                      return onSubmit(
                        {
                          ...values,
                          requestConfirm: statusValue,
                          notifyApplicant: Boolean(options?.notifyApplicant),
                        },
                        { setSubmitting }
                      );
                    }}
                  />
                </form>
              </Fragment>
            );
          }}
        </Formik>
      ) : null}
    </CardContent>
  );

  if (embedded) {
    return <Box>{formBody}</Box>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              requestId !== "create"
                ? `ویرایش درخواست ${request?.fullName || ""}`
                : "ایجاد درخواست جدید"
            }
            subheader="بررسی و ویرایش اطلاعات از بالا به پایین، سپس تصمیم نهایی"
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <Button
                  variant="outlined"
                  color="gray"
                  startIcon={<UndoIcon />}
                  onClick={onNavigateToListPage}
                >
                  بازگشت
                </Button>
              </Box>
            }
          />
          {formBody}
        </Card>
      </Grid>
    </Grid>
  );
};

export default CheckNewRequestModifyPage;
